import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useUser} from "../../providers/user-context";
import axios from "../../config/axios-config";
import {eventBus} from "../../event-bus";

const proposalPath = id => `/api/v1/supplier-orders/order-proposals/${encodeURIComponent(id)}/confirmation`;
export const isConfirmationResolved = view => ["CONFIRMED", "AUTOMATICALLY_CONFIRMED", "REJECTED", "DECLINED", "EXPIRED"].includes(view?.state);
const validView = data => Boolean(data?.proposalId && Array.isArray(data.proposal?.lines) && data.current?.versions
    && Number.isFinite(Date.parse(data.serverTime)) && (data.state !== "AWAITING_CONFIRMATION" || Number.isFinite(Date.parse(data.expiresAt))));

function initialResolutionChangedOrder(data, status) {
    if (["CONFIRMED", "AUTOMATICALLY_CONFIRMED"].includes(data.state)) {
        return Boolean(status) && !["PROCESSING", "SHIPPED", "COMPLETED"].includes(status);
    }
    // A first expiry read may settle the order. Already-cancelled details need no second reload.
    return data.state === "EXPIRED" ? status !== "CANCELLED" : Boolean(status) && status !== "CANCELLED";
}

export default function useOrderConfirmation({open, proposalId, lineId, orderStatus}) {
    const {i18n} = useTranslation(), user = useUser();
    const scope = [open, proposalId, lineId, user.tenantId, user.userIdentity, user.tenantType, user.userRole, i18n.language].join(":");
    const scopeRef = useRef(scope); scopeRef.current = scope;
    const initialStatus = useRef({scope, status: orderStatus});
    // Compare the first proposal read with the opening snapshot, even if a concurrent courier read corrects it first.
    if (initialStatus.current.scope !== scope) { initialStatus.current = {scope, status: orderStatus}; }
    const read = useRef(), write = useRef(), attempt = useRef(), observedResolution = useRef();
    const [state, setState] = useState({}), [deadlinePassed, setDeadlinePassed] = useState(false);
    const current = state.scope === scope ? state : {};
    const permitted = Boolean(user.tenantId && user.userIdentity && ["PHARMACY", "IMPORTER"].includes(user.tenantType));
    const load = useCallback(async () => {
        if (!open || !permitted || (!proposalId && !lineId) || write.current) return;
        read.current?.abort();
        const controller = new AbortController(); read.current = controller;
        const active = () => scopeRef.current === scope && !controller.signal.aborted;
        setState(previous => ({...(previous.scope === scope ? previous : {}), scope, loading: true, error: ""}));
        try {
            const {data} = await axios.get(proposalId ? proposalPath(proposalId)
                : `/api/v1/supplier-orders/by-line/${encodeURIComponent(lineId)}/confirmation`,
            {signal: controller.signal, timeout: 10000, headers: {"Language-Context": i18n.language.toUpperCase()}});
            if (!validView(data)) throw new Error("Invalid confirmation view");
            if (active()) {
                attempt.current = null; setDeadlinePassed(false);
                const resolution = isConfirmationResolved(data) ? `${data.proposalId}:${data.state}` : null;
                // Opening settled history is a read, not a new decision. Preserve expiry and live-transition refreshes.
                const refreshRequired = Boolean(resolution && observedResolution.current !== resolution
                    && (observedResolution.current !== undefined || initialResolutionChangedOrder(data, initialStatus.current.status)));
                setState({scope, data, refreshRequired, receivedAt: performance.now()});
                if (refreshRequired) { eventBus.emit("refreshOrderPage"); }
                observedResolution.current = resolution;
            }
        } catch (error) {
            if (active()) setState(previous => ({...previous, scope, loading: false, conflict: true,
                error: !proposalId && error.response?.status === 404 ? "" : "ConfirmationLoadFailed", data: null}));
        }
    }, [scope, open, permitted, proposalId, lineId, i18n.language]);

    useEffect(() => {
        attempt.current = null; observedResolution.current = undefined; setState({scope}); setDeadlinePassed(false); load();
        return () => {read.current?.abort(); write.current?.abort(); write.current = null;};
    }, [scope, load]);

    useEffect(() => {
        const data = current.data;
        if (data?.state !== "AWAITING_CONFIRMATION" || !data.expiresAt) return;
        const remaining = Date.parse(data.expiresAt) - Date.parse(data.serverTime) - (performance.now() - current.receivedAt);
        // Use the server's clock and monotonic elapsed time; browser clock/timezone changes cannot extend consent.
        const timer = setTimeout(() => {if (remaining <= 2147483647) setDeadlinePassed(true); if (!write.current && !attempt.current) load();}, Math.max(0, Math.min(remaining, 2147483647)));
        return () => clearTimeout(timer);
    }, [current.data, current.receivedAt, load]);

    const submit = async (accept, reason, acceptSplitDeliveries = false) => {
        if (write.current || !current.data || current.conflict || !permitted) return;
        if (!attempt.current && (!current.data.canDecide || deadlinePassed || !accept && !reason?.trim())) return;
        const hasBackorders = current.data.proposal.lines.some(line => Number(line.backorderedQuantity) > 0);
        if (!attempt.current && accept && hasBackorders && !acceptSplitDeliveries) {return;}
        if (!attempt.current) attempt.current = {proposalId: current.data.proposalId,
            body: {requestId: crypto.randomUUID(), accept, expectedVersions: current.data.current.versions, reason: reason?.trim() || null,
                ...(acceptSplitDeliveries ? {acceptSplitDeliveries: true} : {})}};
        const saved = attempt.current, controller = new AbortController(); write.current = controller;
        read.current?.abort();
        const active = () => scopeRef.current === scope && !controller.signal.aborted;
        setState(previous => ({...previous, pending: true, error: ""}));
        try {
            const {data} = await axios.post(proposalPath(saved.proposalId), saved.body,
                {signal: controller.signal, timeout: 15000, headers: {"Language-Context": i18n.language.toUpperCase()}});
            if (data?.requestId !== saved.body.requestId || !validView(data.confirmation) || data.confirmation.proposalId !== saved.proposalId)
                throw new Error("Invalid confirmation receipt");
            if (active()) {attempt.current = null; setState({scope, data: data.confirmation, receivedAt: performance.now()}); eventBus.emit("refreshOrderPage");}
        } catch (error) {
            if (active()) {
                if (error.response?.data?.confirmation?.state === "EXPIRED") {
                    attempt.current = null; setState({scope, data: error.response.data.confirmation, receivedAt: performance.now()}); eventBus.emit("refreshOrderPage");
                } else {
                    const uncertain = !error.response || error.response.status >= 500;
                    if (!uncertain) attempt.current = null;
                    setState(previous => ({...previous, pending: false, uncertain, conflict: !uncertain,
                        error: uncertain ? "ConfirmationUncertain" : "ConfirmationConflict"}));
                }
            }
        } finally {if (write.current === controller) write.current = null;}
    };
    return {...current, permitted, deadlinePassed, refresh: load, submit, retry: () => submit(),
        canDecide: Boolean(current.data?.canDecide && !deadlinePassed && !current.conflict && !current.pending && !current.loading && !current.uncertain)};
}
